// © 2026 KaraokeClubs. All rights reserved.

"use client";

import { toZonedTime } from "date-fns-tz";
import { getKaraokeHours } from "@/lib/venue-helpers";
import type { Venue, DayHours } from "@/lib/types";

const WARSAW_TZ = "Europe/Warsaw";
const DAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"] as const;
const DAY_LABELS = ["M", "T", "W", "T", "F", "S", "S"] as const;

function getTodayKey(): string {
  const now = toZonedTime(new Date(), WARSAW_TZ);
  return (["sun", "mon", "tue", "wed", "thu", "fri", "sat"] as const)[now.getDay()];
}

function formatStart(dh: DayHours): string {
  return dh.open.replace(/:00$/, "");
}

export default function WeeklyGrid({ venue }: { venue: Venue }) {
  const hours = getKaraokeHours(venue);
  if (!hours) return null;

  const today = getTodayKey();
  const hasAny = DAYS.some((d) => hours[d] !== null);
  if (!hasAny) return null;

  return (
    <div className="flex gap-[3px]">
      {DAYS.map((day, i) => {
        const dh = hours[day];
        const isToday = day === today;
        const hasKaraoke = dh !== null;

        return (
          <div
            key={day}
            className={`flex flex-col items-center rounded-md px-[5px] py-[3px] min-w-[26px] transition-colors ${
              hasKaraoke
                ? isToday
                  ? "bg-emerald-500/20 border border-emerald-500/30"
                  : "bg-violet-500/10 border border-violet-500/15"
                : "bg-transparent border border-transparent"
            }`}
          >
            <span
              className={`text-[9px] font-bold leading-none ${
                hasKaraoke
                  ? isToday
                    ? "text-emerald-400"
                    : "text-violet-400"
                  : "text-zinc-600"
              }`}
            >
              {DAY_LABELS[i]}
            </span>
            {hasKaraoke && dh ? (
              <span
                className={`text-[9px] tabular-nums leading-none mt-[2px] font-semibold ${
                  isToday ? "text-emerald-300" : "text-zinc-400"
                }`}
              >
                {formatStart(dh)}
              </span>
            ) : (
              <span className="text-[9px] leading-none mt-[2px] text-zinc-700">–</span>
            )}
          </div>
        );
      })}
    </div>
  );
}
